import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ExpectedGuest, Unit } from '@/api/pending'
import { queryKeys } from '@/api/queryKeys'
import { expectedForUnit } from '@/lib/desk'
import { Button, Caution, Chip, Eyebrow, Field, Icon, Modal, useToast } from '@/ui'
import { expectedGuestsQuery, registerGuests, unitsWithResidentsQuery } from './api'
import styles from './desk.module.css'
import { deskStrings } from './strings'
import { UnitPad } from './UnitPad'

interface Props {
  condoId: number
  /** "Registrar chegada" on an expected guest: unit, name and document come filled in. */
  prefill?: ExpectedGuest
  onClose: () => void
}

export function VisitorModal({ condoId, prefill, onClose }: Props) {
  const qc = useQueryClient()
  const toast = useToast()
  const units = useQuery(unitsWithResidentsQuery(condoId))
  const expected = useQuery(expectedGuestsQuery(condoId))
  const prefillUnit = prefill
    ? units.data?.find((u) => u.number === prefill.unit && u.tower === prefill.tower)
    : undefined

  const [picked, setPicked] = useState<Unit | null>(null)
  const unit = picked ?? prefillUnit ?? null
  const [name, setName] = useState(prefill?.guest_name ?? '')
  const [taxId, setTaxId] = useState(prefill?.guest_tax_id ?? '')
  const [chosenId, setChosenId] = useState<string | undefined>(prefill?.id)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const t = deskStrings.visitor

  const unitExpected = expectedForUnit(expected.data ?? [], unit)
  // Editing the name detaches the registration from the expected guest.
  const chosen = unitExpected.find((g) => g.id === chosenId && g.guest_name === name.trim())

  async function submit() {
    if (!unit) return
    setBusy(true)
    setError('')
    try {
      await registerGuests(condoId, {
        unit_id: unit.id,
        guests: [
          {
            guest_name: name.trim(),
            ...(taxId.trim() ? { guest_tax_id: taxId.trim() } : {}),
            ...(chosen ? { expected_guest_id: chosen.id } : {}),
          },
        ],
      })
      await qc.invalidateQueries({ queryKey: queryKeys.desk.all(condoId) })
      toast(`${name.trim()} registrado para ${unit.number}`)
      onClose()
    } catch {
      setError(t.failed)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      width={560}
      title={t.title}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            icon="check"
            disabled={!unit || !name.trim()}
            loading={busy}
            onClick={() => void submit()}
          >
            {t.submit}
          </Button>
        </>
      }
    >
      <UnitPad units={units.data} value={unit?.id} onPick={setPicked} />
      {unitExpected.length > 0 && (
        <div className={styles.pad}>
          <Eyebrow>{t.expectedHere}</Eyebrow>
          <div className={styles.chips}>
            {unitExpected.map((g) => (
              <Chip
                key={g.id}
                selected={chosen?.id === g.id}
                onClick={() => {
                  setName(g.guest_name)
                  setTaxId(g.guest_tax_id)
                  setChosenId(g.id)
                }}
              >
                {g.guest_name}
              </Chip>
            ))}
          </div>
        </div>
      )}
      <Field label={t.name} value={name} onChange={setName} placeholder={t.namePlaceholder} />
      <Field
        label={t.document}
        value={taxId}
        onChange={setTaxId}
        placeholder={t.documentPlaceholder}
        hint={t.documentHint}
      />
      <Caution>{t.note}</Caution>
      {error && (
        <div className={styles.error} role="alert">
          <Icon name="error" size={16} />
          {error}
        </div>
      )}
    </Modal>
  )
}
